import DOMPurify from 'dompurify';

// Document data from share links (?dt=), public share/print payloads, the ERP
// document API and saved template settings is rendered by the templates through
// dangerouslySetInnerHTML, so it is untrusted HTML. Strip anything
// script-capable before it reaches state. Any field that is decoded later
// (decodeURIComponent, atob, ...) must be sanitized again after decoding.

// An element can only be created by '<' followed by a letter. A trailing '<' is
// included because templates concatenate fields, and "x <" + "img ..." would
// open a tag. Other strings are returned untouched, so plain text such as
// "a & b" or "size < 5mm" is not entity-encoded where it is rendered as text.
const MARKUP = /<([a-z]|$)/i;

// Browsers skip leading control characters/spaces and drop tabs and newlines
// anywhere in a URL, so " java\tscript:" still runs. Such a string is only
// ever useful as a link, so it is blanked.
const SCRIPT_URL = /^(javascript|vbscript):/i;

const CONFIG = {
  // Global CSS and form controls have no place in a printed document and would
  // allow page restyling or a fake login form on the Deskera origin.
  FORBID_TAGS: ['style', 'form', 'input', 'button', 'textarea', 'select', 'option'],
  // id/name let injected markup shadow the templates' own elements and globals.
  FORBID_ATTR: ['id', 'name'],
};

function escapeHtml(value) {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function sanitizeHtmlString(value) {
  if (typeof value !== 'string') {
    return value;
  }
  // eslint-disable-next-line no-control-regex
  if (SCRIPT_URL.test(value.replace(/^[\u0000- ]+/, '').replace(/[\t\n\r]/g, ''))) {
    return '';
  }
  if (!MARKUP.test(value)) {
    return value;
  }
  // Some fields hold serialised JSON; sanitizing the raw text as HTML would
  // corrupt it, so clean the parsed values and serialise them back.
  const trimmed = value.trim();
  if (trimmed[0] === '{' || trimmed[0] === '[') {
    try {
      return JSON.stringify(sanitizeDocumentData(JSON.parse(trimmed)));
    } catch (error) {
      // not JSON, sanitize as HTML below
    }
  }
  // Without a working DOM, DOMPurify returns its input unchanged; fail closed.
  if (!DOMPurify.isSupported) {
    return escapeHtml(value);
  }
  return DOMPurify.sanitize(value, CONFIG);
}

export function sanitizeDocumentData(data) {
  if (typeof data === 'string') {
    return sanitizeHtmlString(data);
  }
  if (Array.isArray(data)) {
    for (let i = 0; i < data.length; i++) {
      data[i] = sanitizeDocumentData(data[i]);
    }
    return data;
  }
  if (data !== null && typeof data === 'object') {
    Object.keys(data).forEach((key) => {
      if (key === '__proto__') {
        delete data[key];
        return;
      }
      data[key] = sanitizeDocumentData(data[key]);
    });
  }
  return data;
}
